import { invariant } from 'ts-invariant';
import type { BlockType } from '@unifyapps/defs/types/block';
import { useGetGlobalStateStoreState } from '../../../stores/GlobalStateStore';
import { getBlocksFromPage, useGetInterfaceStoreState } from '../../../stores/InterfaceStore';
import type { UnsavedDataSource } from '../../../stores/DataSourceRecordStore';
import { useDataSourceRecordStore } from '../../../stores/DataSourceRecordStore';
import type { DependencyGraphManager } from '../../../../dependency/manager/DependencyGraphManager';
import {
  blockDependencyBuilder,
  dataSourceDependencyBuilder,
  pageFunctionDependencyBuilder,
} from '../builders';
import { BlockHelper } from '../../../../helpers/BlockHelper';
import { PageFunctionHelper } from '../../../../helpers/PageFunctionHelper';
import DataSourceHelper from '../../../helper/DataSourceHelper';
import { useRegistryContext } from '../../../components/RegistryProvider';
import { buildBlockImplicitDependencies } from '../../../dependency/buildBlockImplicitDependencies';
import type { EntityListType } from './useBuildEntitiesForDependencies';

export function useHandleSavedDependencies() {
  const { getGlobalStateStoreState } = useGetGlobalStateStoreState();
  const { getInterfaceStoreState } = useGetInterfaceStoreState();
  const getDataSourceRecordStoreState = useDataSourceRecordStore().getState;
  const activeInterfacePage =
    getInterfaceStoreState().interfacePages[getInterfaceStoreState().activeInterfacePageId];
  const { registry } = useRegistryContext();

  function buildEntityDataSourceDependencyGraph({
    entityDataSources,
    graphManager,
  }: {
    entityDataSources: Record<string, UnsavedDataSource | undefined>;
    graphManager: DependencyGraphManager;
  }) {
    Object.entries(entityDataSources).forEach(([_, entityDataSource]) => {
      if (!entityDataSource) return;

      const dataSourceDependencyGraph = dataSourceDependencyBuilder({
        dataSource: entityDataSource,
      });

      graphManager.updateNodes(dataSourceDependencyGraph);
    });
  }

  function buildBlocksImplicitDependencies({
    pageBlocks,
    graphManager,
  }: {
    pageBlocks: Record<string, BlockType | undefined>;
    graphManager: DependencyGraphManager;
  }) {
    Object.values(pageBlocks).forEach((block) => {
      if (!block) return;
      const deps = buildBlockImplicitDependencies({ block, registry });

      graphManager.mergeNode(block.id, deps);
    });
  }

  function handleSavedDependencies({
    entityList,
    graphManager,
  }: {
    entityList: EntityListType;
    graphManager: DependencyGraphManager;
  }) {
    // get the page blocks handling the current view device in mind
    const pageBlocks = getBlocksFromPage(
      getGlobalStateStoreState().deviceDetails.device,
      getInterfaceStoreState(),
    );

    // 3.1 add the explicit block dependencies that are saved in the page
    Object.values(pageBlocks).forEach((block) => {
      if (!block) return;
      graphManager.updateNode(block.id, BlockHelper.getDependsOn(block));
    });

    // 3.2 add the explicit page function dependencies that are saved in the page
    const pageFunctions = activeInterfacePage.properties.pageFunctions;
    Object.entries(pageFunctions ?? {}).forEach(([pageFunctionId, pageFunction]) => {
      graphManager.updateNode(pageFunctionId, PageFunctionHelper.getDependsOn(pageFunction));
    });

    // 3.3 add the explicit datasource dependencies that are saved in the page
    const dataSourceRecords = getDataSourceRecordStoreState().dataSources;
    Object.entries(dataSourceRecords).forEach(([dataSourceId, dataSource]) => {
      graphManager.updateNode(dataSourceId, DataSourceHelper.getDependsOn(dataSource));
    });

    // 3.4 build entity datasource dependency graph, because we don't store them
    buildEntityDataSourceDependencyGraph({
      entityDataSources: entityList.entityDataSources,
      graphManager,
    });

    // 3.5 blocks also need more dependencies from another part of the context which is not mentioned by the user,
    // so we are adding them manually here
    buildBlocksImplicitDependencies({
      pageBlocks,
      graphManager,
    });
  }

  function buildNewDependencies({
    entityList,
    graphManager,
  }: {
    entityList: EntityListType;
    graphManager: DependencyGraphManager;
  }) {
    // get the page blocks handling the current view device in mind
    const pageBlocks = getBlocksFromPage(
      getGlobalStateStoreState().deviceDetails.device,
      getInterfaceStoreState(),
    );

    //3.1 build block dependency graph
    Object.entries(entityList.blocks).forEach(([, blockState]) => {
      if (!blockState) return;

      const blockId = blockState.originalBlockId ?? blockState.id;
      if (!blockId) return;

      const pageBlock = pageBlocks[blockId];
      invariant(pageBlock, 'Page block not found');
      const blockDependencyGraph = blockDependencyBuilder({
        blockState,
        pageBlock,
      });

      graphManager.updateNodes(blockDependencyGraph);
    });

    //3.2 build page function dependency graph
    Object.entries(entityList.pageFunctions).forEach(([, pageFunction]) => {
      const pageFunctionDependencyGraph = pageFunctionDependencyBuilder({
        pageFunction,
      });

      graphManager.updateNodes(pageFunctionDependencyGraph);
    });

    //3.4 build datasource dependency graph
    Object.entries(entityList.dataSources).forEach(([, dataSource]) => {
      if (!dataSource) return;

      const dataSourceDependencyGraph = dataSourceDependencyBuilder({
        dataSource,
      });

      graphManager.updateNodes(dataSourceDependencyGraph);
    });

    // 3.5 build entity datasource dependency graph
    buildEntityDataSourceDependencyGraph({
      entityDataSources: entityList.entityDataSources,
      graphManager,
    });

    // 3.6 blocks also need more dependencies from another part of the context which is not mentioned by the user,
    // so we are adding them manually here
    buildBlocksImplicitDependencies({ pageBlocks, graphManager });
  }

  return { handleSavedDependencies, buildNewDependencies };
}
